import { type FC, useMemo } from "react";

import type { UsageTotals } from "../../types/usage";

import {
  formatTokenCount,
  formatTokenHours,
  formatUsd,
  toFiniteNumber,
} from "../../utils/currency";
import {
  formatPercentage,
  getInputOutputRatio,
} from "../../utils/usageMetrics";
import { MetricCard } from "../common/MetricCard";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";
import { UsageModalityCoveragePanel } from "./UsagePricingDetails";

export interface UsageSecondaryMetricsProps {
  totals: UsageTotals | null;
}

function TokenStat(props: { label: string; subValue?: string; value: string }) {
  return (
    <div className="min-w-0">
      <Text variant="caption" as="p">
        {props.label}
      </Text>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
        {props.value}
      </p>
      {props.subValue ? (
        <Text as="p" className="mt-0.5">
          {props.subValue}
        </Text>
      ) : null}
    </div>
  );
}

export const UsageSecondaryMetrics: FC<UsageSecondaryMetricsProps> = ({
  totals,
}) => {
  const inputCost = toFiniteNumber(totals?.costBreakdown?.inputUsd);
  const outputCost = toFiniteNumber(totals?.costBreakdown?.outputUsd);
  const cachedInputCost = toFiniteNumber(totals?.costBreakdown?.cachedInputUsd);
  const cacheWriteCost = toFiniteNumber(totals?.costBreakdown?.cacheWriteUsd);
  const cacheStorageCost = toFiniteNumber(
    totals?.costBreakdown?.cacheStorageUsd,
  );
  const totalCost = toFiniteNumber(totals?.costUsd);

  const promptTokens = totals?.promptTokens ?? 0;
  const completionTokens = totals?.completionTokens ?? 0;
  const uncachedInputTokens = toFiniteNumber(totals?.uncachedInputTokens);
  const cachedInputTokens = totals?.cachedInputTokens ?? 0;
  const cacheWriteTokens = totals?.cacheWriteTokens ?? 0;
  const cacheStorageTokenHours = toFiniteNumber(totals?.cacheStorageTokenHours);
  const tokenRatio = useMemo(
    () => getInputOutputRatio(promptTokens, completionTokens),
    [promptTokens, completionTokens],
  );

  const inputCostPercentage = formatPercentage(inputCost, totalCost);
  const outputCostPercentage = formatPercentage(outputCost, totalCost);
  const cachedInputPercentage = formatPercentage(
    cachedInputTokens,
    toFiniteNumber(promptTokens),
  );
  const uncachedInputPercentage = formatPercentage(
    uncachedInputTokens,
    toFiniteNumber(promptTokens),
  );
  const hasModalityCoverage =
    Boolean(totals?.inputTokensByModality) ||
    Boolean(totals?.outputTokensByModality);

  if (!totals) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Cost & Token Details"
        icon={
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <MetricCard
          label="Input Cost"
          value={formatUsd(inputCost)}
          subValue={
            inputCostPercentage ? `${inputCostPercentage} of total` : undefined
          }
          variant="emerald"
          size="lg"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
          }
        />

        <MetricCard
          label="Output Cost"
          value={formatUsd(outputCost)}
          subValue={
            outputCostPercentage
              ? `${outputCostPercentage} of total`
              : undefined
          }
          variant="indigo"
          size="lg"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          }
        />
      </div>

      <div className="space-y-2">
        <Text variant="label" as="p">
          Cache Costs
        </Text>
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard
            label="Cached Input"
            value={formatUsd(cachedInputCost)}
            subValue={`${formatTokenCount(cachedInputTokens)} tokens`}
            variant="amber"
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                />
              </svg>
            }
          />

          <MetricCard
            label="Cache Write"
            value={formatUsd(cacheWriteCost)}
            subValue={`${formatTokenCount(cacheWriteTokens)} tokens`}
            variant="rose"
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            }
          />

          <MetricCard
            label="Cache Storage"
            value={formatUsd(cacheStorageCost)}
            subValue={
              cacheStorageTokenHours !== null
                ? formatTokenHours(cacheStorageTokenHours)
                : undefined
            }
            variant="violet"
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"
                />
              </svg>
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Text variant="label" as="p">
          Token Breakdown
        </Text>
        <div className="rounded-none border border-slate-300 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-arti-panel">
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
            <TokenStat
              label="Prompt"
              value={formatTokenCount(promptTokens)}
              subValue={
                tokenRatio
                  ? `${tokenRatio.input.toFixed(1)}% of total`
                  : undefined
              }
            />
            <TokenStat
              label="Completion"
              value={formatTokenCount(completionTokens)}
              subValue={
                tokenRatio
                  ? `${tokenRatio.output.toFixed(1)}% of total`
                  : undefined
              }
            />
            <TokenStat
              label="Uncached Input"
              value={formatTokenCount(uncachedInputTokens)}
              subValue={
                uncachedInputPercentage
                  ? `${uncachedInputPercentage} of input`
                  : undefined
              }
            />
            <TokenStat
              label="Cached Input"
              value={formatTokenCount(cachedInputTokens)}
              subValue={
                cachedInputPercentage
                  ? `${cachedInputPercentage} of input`
                  : undefined
              }
            />
            <TokenStat
              label="Cache Write"
              value={formatTokenCount(cacheWriteTokens)}
              subValue={
                cacheStorageTokenHours !== null
                  ? `Storage: ${formatTokenHours(cacheStorageTokenHours)}`
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {hasModalityCoverage && (
        <UsageModalityCoveragePanel
          inputTokensByModality={totals.inputTokensByModality}
          outputTokensByModality={totals.outputTokensByModality}
        />
      )}
    </div>
  );
};
